import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, ArrowLeft, CheckCircle, Shield, AlertCircle, Check, Lock, ChevronRight } from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../providers/TranslationProvider';
import { checkPasswordStrength } from '../utils/validation';
import { SCREENS } from '../utils/constants';
import LetterGlitch from '../components/LetterGlitch';
import Logo from '../icons/logo.svg';

const NewPasswordScreen = () => {
  const { navigateToScreen } = useUI();
  const { resetPassword } = useAuth(); // Assuming this is available
  const { t } = useTranslation();

  // Form states
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Theme state
  const [isDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const passwordStrength = useMemo(() => checkPasswordStrength(password), [password]);

  const handleReset = async (e) => {
    e.preventDefault();
    setLocalError('');

    if (!password || !confirmPassword) {
      setLocalError(t('password.error.emptyFields', 'Please fill all fields.'));
      return;
    }

    if (password !== confirmPassword) {
      setLocalError(t('password.error.mismatch', 'Passwords do not match.'));
      return;
    }

    if (passwordStrength.score < 3) {
      setLocalError(t('password.error.tooWeak', `Password is too weak: ${passwordStrength.message}`));
      return;
    }

    setIsLoading(true);

    try {
      // await resetPassword(password);
      await new Promise(resolve => setTimeout(resolve, 1500)); // Mock
      setSuccess(true);
      setTimeout(() => {
        navigateToScreen(SCREENS.LOGIN);
      }, 2000);
    } catch (err) {
      console.error(err);
      setLocalError(err.message || t('password.error.failed', 'Failed to reset password.'));
    } finally {
      setIsLoading(false);
    }
  };

  // Animation Variants
  const containerVariants = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
    exit: { opacity: 0, x: -20, transition: { duration: 0.3 } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut", delay: 0.2 } }
  };

  const bgClass = isDarkMode ? 'bg-slate-900' : 'bg-slate-50';
  const textClass = isDarkMode ? 'text-white' : 'text-slate-900';
  const inputBg = isDarkMode ? 'bg-slate-800/50 border-slate-700 text-white placeholder-slate-500' : 'bg-white border-blue-100 text-slate-800 placeholder-slate-400';
  const cardBg = isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-white/80 border-white';

  const getStrengthColor = (score) => {
    if (score < 2) return 'bg-red-500';
    if (score < 4) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <motion.div
      className={`relative w-full h-screen overflow-hidden flex flex-col items-center justify-center ${bgClass} ${textClass}`}
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Dynamic Background */}
      <div className={`absolute inset-0 transition-colors duration-700 ${isDarkMode ? 'bg-slate-950' : 'bg-gradient-to-br from-blue-50 to-white'}`}>
        {isDarkMode && (
          <div className="absolute inset-0 opacity-30 pointer-events-none">
            <LetterGlitch
              glitchColors={['#1e40af', '#3b82f6', '#60a5fa']}
              glitchSpeed={50}
              centerVignette={true}
              outerVignette={false}
              smooth={true}
            />
          </div>
        )}
      </div>

      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5 }}
        onClick={() => navigateToScreen(SCREENS.LOGIN)}
        className="absolute top-6 left-6 z-20 p-2 rounded-full hover:bg-white/10 transition-colors"
        aria-label="Back"
      >
        <ArrowLeft size={24} className={isDarkMode ? 'text-white' : 'text-slate-800'} />
      </motion.button>

      {/* Main Content */}
      <motion.div
        className={`relative z-10 w-full max-w-md p-8 rounded-3xl backdrop-blur-xl border shadow-2xl ${cardBg}`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="flex flex-col items-center mb-6">
          <div className={`w-16 h-16 mb-4 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-slate-800 shadow-lg shadow-blue-900/20' : 'bg-white shadow-xl shadow-blue-100'}`}>
            <Lock className="w-8 h-8 text-blue-500" />
          </div>
          <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-600">
            {t('password.newTitle', 'Set New Password')}
          </h2>
          <p className={`text-sm mt-1 text-center ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {t('password.newSubtitle', 'Create a strong password for your account')}
          </p>
        </div>

        {!success ? (
          <form onSubmit={handleReset} className="space-y-4">

            {/* New Password */}
            <div className="space-y-1">
              <label className={`text-xs ml-1 font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {t('password.newLabel', 'New Password')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`w-full px-5 py-3.5 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all border ${inputBg}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-500 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {/* Strength Meter */}
              {password && (
                <div className="mt-1 h-1 w-full bg-gray-200/20 rounded-full overflow-hidden flex">
                  <div className={`h-full transition-all duration-300 ${getStrengthColor(passwordStrength.score)}`} style={{ width: `${(passwordStrength.score / 5) * 100}%` }} />
                </div>
              )}
              <p className={`text-xs mt-1 ${passwordStrength.score < 3 ? 'text-yellow-500' : 'text-green-500'
                }`}>
                {passwordStrength.message}
              </p>
            </div>

            {/* Confirm Password */}
            <div className="space-y-1">
              <label className={`text-xs ml-1 font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {t('password.confirmLabel', 'Confirm Password')}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`w-full px-5 py-3.5 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 transition-all border ${inputBg}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-500 transition-colors"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            <AnimatePresence>
              {localError && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm"
                >
                  <AlertCircle size={16} />
                  <span>{localError}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={isLoading}
              type="submit"
              className={`w-full mt-4 py-4 rounded-xl font-bold text-lg shadow-lg relative overflow-hidden group ${isDarkMode
                  ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-blue-900/30'
                  : 'bg-blue-600 text-white shadow-blue-200'
                }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('common.processing', 'Updating...')}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {t('password.update', 'Update Password')}
                  <ChevronRight size={20} />
                </span>
              )}
            </motion.button>
          </form>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="w-24 h-24 mx-auto bg-green-500/10 rounded-full flex items-center justify-center mb-6">
              <CheckCircle size={48} className="text-green-500" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('password.success', 'Password Updated!')}</h3>
            <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {t('password.redirect', 'Redirecting you to login...')}
            </p>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default NewPasswordScreen;